import villainsManifest from "../generated/villainsManifest.json";

export const VILLAIN_INTERRUPTION_CONFIG = {
  DELAY_THRESHOLD_MS: 4500,
  MIN_ELIGIBLE_DELAYS_BEFORE_TRIGGER: 2,
  TRIGGER_CHANCE_AFTER_MIN: 0.25,
  EVENT_COOLDOWN_MS: 120000,
  DUCKING_FACTOR: 0.28,
  RECENT_HISTORY_SIZE: 4,
  FADE_MS: 120,
};

function clampVolume(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 1;
  return Math.min(1, Math.max(0, numeric));
}

function normalizeLang(lang) {
  const raw = String(lang || "").trim().toLowerCase();
  if (raw.startsWith("pt")) return "pt";
  if (raw.startsWith("en")) return "en";
  if (raw.startsWith("es")) return "es";
  return "";
}

function getRefCurrent(refOrElement) {
  if (!refOrElement) return null;
  if (Object.prototype.hasOwnProperty.call(refOrElement, "current")) {
    return refOrElement.current || null;
  }
  return refOrElement;
}

function hasUsableSource(audio) {
  return Boolean(String(audio?.currentSrc || audio?.src || "").trim());
}

function getActivePlayers(backgroundAudioRefs) {
  const refs = Array.isArray(backgroundAudioRefs) ? backgroundAudioRefs : [];
  const seen = new Set();
  const players = [];

  refs.forEach((ref) => {
    const audio = getRefCurrent(ref);
    if (!audio || seen.has(audio)) return;
    seen.add(audio);
    if (audio.paused === true) return;
    if (!hasUsableSource(audio)) return;
    if (typeof audio.volume !== "number") return;
    players.push(audio);
  });

  return players;
}

function flattenTracksForLang(manifest, lang) {
  const langManifest = manifest?.[lang];
  if (!langManifest || typeof langManifest !== "object") return [];
  const tracks = [];
  Object.keys(langManifest)
    .sort()
    .forEach((villain) => {
      const villainTracks = Array.isArray(langManifest[villain]) ? langManifest[villain] : [];
      villainTracks.forEach((url) => {
        const src = String(url || "").trim();
        if (src) tracks.push({ villain, src });
      });
    });
  return tracks;
}

function pickTrack({ manifest, lang, recent, recentHistorySize, random }) {
  const normalizedLang = normalizeLang(lang);
  if (!normalizedLang) return null;

  const allTracks = flattenTracksForLang(manifest, normalizedLang);
  if (!allTracks.length) return null;

  const uniqueSources = Array.from(new Set(allTracks.map((item) => item.src)));
  const shouldAvoidRecent = uniqueSources.length > recentHistorySize;
  const recentSet = new Set(recent);
  const candidates = shouldAvoidRecent
    ? allTracks.filter((item) => !recentSet.has(item.src))
    : allTracks;
  const pool = candidates.length > 0 ? candidates : allTracks;

  return pool[Math.floor(random() * pool.length)] || null;
}

function createFadeVolume(setTimeoutFn) {
  return function fadeVolume(audio, targetVolume, fadeMs) {
    const target = clampVolume(targetVolume);
    if (!audio || typeof audio.volume !== "number") return Promise.resolve();
    const start = clampVolume(audio.volume);
    const duration = Math.max(0, Number(fadeMs || 0));
    if (duration <= 0 || Math.abs(start - target) < 0.001) {
      audio.volume = target;
      return Promise.resolve();
    }

    const steps = 4;
    const stepMs = Math.max(1, Math.floor(duration / steps));
    let step = 0;

    return new Promise((resolve) => {
      const tick = () => {
        step += 1;
        const ratio = Math.min(1, step / steps);
        audio.volume = clampVolume(start + (target - start) * ratio);
        if (ratio >= 1) {
          resolve();
          return;
        }
        setTimeoutFn(tick, stepMs);
      };
      setTimeoutFn(tick, stepMs);
    });
  };
}

export function createVillainInterruptionManager(options = {}) {
  const config = {
    ...VILLAIN_INTERRUPTION_CONFIG,
    ...(options.config || {}),
  };
  const manifest = options.manifest || villainsManifest;
  const random = typeof options.random === "function" ? options.random : Math.random;
  const now = typeof options.now === "function" ? options.now : () => Date.now();
  const browserWindow = typeof window !== "undefined" ? window : null;
  const setTimeoutFn =
    typeof options.setTimeoutFn === "function"
      ? options.setTimeoutFn
      : browserWindow
        ? browserWindow.setTimeout.bind(browserWindow)
        : setTimeout;
  const clearTimeoutFn =
    typeof options.clearTimeoutFn === "function"
      ? options.clearTimeoutFn
      : browserWindow
        ? browserWindow.clearTimeout.bind(browserWindow)
        : clearTimeout;
  const AudioCtor =
    options.AudioCtor ||
    (browserWindow && typeof browserWindow.Audio === "function"
      ? browserWindow.Audio
      : null);
  const fadeVolume = createFadeVolume(setTimeoutFn);

  let requestSeq = 0;
  let activeRequest = null;
  let eligibleDelayCount = 0;
  let cooldownUntil = 0;
  let playing = false;
  let villainAudio = null;
  const recent = [];

  function clearActiveTimer() {
    if (activeRequest?.timerId != null) {
      clearTimeoutFn(activeRequest.timerId);
      activeRequest.timerId = null;
    }
  }

  function invalidatePendingRequest() {
    clearActiveTimer();
    if (activeRequest) {
      activeRequest.cancelled = true;
    }
    activeRequest = null;
  }

  function reduceCounterAfterMiss() {
    eligibleDelayCount = Math.max(2, eligibleDelayCount - 1);
  }

  function rememberTrack(src) {
    recent.push(src);
    while (recent.length > config.RECENT_HISTORY_SIZE) {
      recent.shift();
    }
  }

  async function restoreVolumes(capturedPlayers) {
    await Promise.all(
      capturedPlayers.map((item) => fadeVolume(item.audio, item.originalVolume, config.FADE_MS))
    );
  }

  async function playVillainTrack(track, backgroundAudioRefs) {
    if (!AudioCtor || !track?.src) return false;
    playing = true;

    const capturedPlayers = getActivePlayers(backgroundAudioRefs).map((audio) => ({
      audio,
      originalVolume: clampVolume(audio.volume),
    }));

    villainAudio = new AudioCtor(track.src);

    try {
      await Promise.all(
        capturedPlayers.map((item) =>
          fadeVolume(
            item.audio,
            item.originalVolume * config.DUCKING_FACTOR,
            config.FADE_MS
          )
        )
      );

      await new Promise((resolve, reject) => {
        const audio = villainAudio;
        if (!audio) {
          reject(new Error("Villain audio unavailable."));
          return;
        }

        const cleanup = () => {
          audio.onended = null;
          audio.onerror = null;
        };
        audio.onended = () => {
          cleanup();
          resolve();
        };
        audio.onerror = () => {
          cleanup();
          reject(new Error("Villain audio failed."));
        };

        const playResult = audio.play();
        if (playResult && typeof playResult.then === "function") {
          playResult.catch((error) => {
            cleanup();
            reject(error);
          });
        }
      });
      rememberTrack(track.src);
      return true;
    } catch {
      return false;
    } finally {
      await restoreVolumes(capturedPlayers);
      villainAudio = null;
      playing = false;
    }
  }

  function tryTrigger(ctx) {
    if (playing) {
      reduceCounterAfterMiss();
      return;
    }
    if (now() < cooldownUntil) {
      reduceCounterAfterMiss();
      return;
    }
    if (typeof ctx.isBlocked === "function" && ctx.isBlocked()) {
      reduceCounterAfterMiss();
      return;
    }

    const track = pickTrack({
      manifest,
      lang: ctx.lang,
      recent,
      recentHistorySize: config.RECENT_HISTORY_SIZE,
      random,
    });
    if (!track) {
      reduceCounterAfterMiss();
      return;
    }

    if (random() >= config.TRIGGER_CHANCE_AFTER_MIN) {
      reduceCounterAfterMiss();
      return;
    }

    eligibleDelayCount = 0;
    cooldownUntil = now() + config.EVENT_COOLDOWN_MS;
    playVillainTrack(track, ctx.backgroundAudioRefs);
  }

  function onDelayThreshold(token, ctx) {
    if (!activeRequest || activeRequest.token !== token || activeRequest.cancelled) return;
    if (activeRequest.counted) return;

    activeRequest.counted = true;
    activeRequest.timerId = null;
    eligibleDelayCount += 1;

    if (eligibleDelayCount < config.MIN_ELIGIBLE_DELAYS_BEFORE_TRIGGER) return;
    tryTrigger(ctx);
  }

  function notifyRequestStarted(ctx = {}) {
    invalidatePendingRequest();
    const token = requestSeq + 1;
    requestSeq = token;
    activeRequest = {
      token,
      counted: false,
      cancelled: false,
      timerId: null,
    };
    activeRequest.timerId = setTimeoutFn(() => {
      onDelayThreshold(token, {
        lang: ctx.lang,
        backgroundAudioRefs: ctx.backgroundAudioRefs,
        isBlocked: ctx.isBlocked,
      });
    }, config.DELAY_THRESHOLD_MS);
  }

  function notifyResponseFinished() {
    invalidatePendingRequest();
  }

  function cancelPendingVillainInterruption() {
    invalidatePendingRequest();
  }

  function isVillainInterruptionPlaying() {
    return playing;
  }

  function __getDebugState() {
    return {
      activeRequest: activeRequest ? { ...activeRequest, timerId: Boolean(activeRequest.timerId) } : null,
      cooldownUntil,
      eligibleDelayCount,
      playing,
      recent: [...recent],
    };
  }

  return {
    notifyRequestStarted,
    notifyResponseFinished,
    cancelPendingVillainInterruption,
    isVillainInterruptionPlaying,
    __getDebugState,
  };
}

const singleton = createVillainInterruptionManager();

export const notifyRequestStarted = singleton.notifyRequestStarted;
export const notifyResponseFinished = singleton.notifyResponseFinished;
export const cancelPendingVillainInterruption = singleton.cancelPendingVillainInterruption;
export const isVillainInterruptionPlaying = singleton.isVillainInterruptionPlaying;

export default singleton;
